import { useAuth0 } from '@auth0/auth0-react'
import { Selector, SelectorGroup } from '@designsforhealth/dfh-react-components'
import React, { FC, useEffect } from 'react'
import { UseFormMethods } from 'react-hook-form'
import { useId } from 'react-id-generator'
import styled from 'styled-components'

import { ConfigurableProductDetailsFragment } from '../../../graphql/magento'
import { ProductLabelStatus } from '../../../graphql/search'
import useSiteMetadata from '../../../hooks/useSiteMetadata'
import { useDispensaryContext } from '../../../lib/dispensaries'
import {
  AttributeName,
  AttributeSkuMapping,
  AttributeValue,
  FormattedVariantData,
} from '../../../utils/itemTools'
import LabelStatusIcon from '../../LabelStatusIcon'
import ItemSelectionIndicatorMessage from './ItemSelectionIndicatorMessage'
import {
  ItemSelectorOptionLabel,
  ItemSelectorOptionLabelWithPrice,
  PriceLabelPrice,
} from './ItemSelectorOptionLabel'

const SelectorGroupWrapper = styled.span`
  display: flex;
  flex-direction: column;
`

const StyledSelectorGroup = styled(SelectorGroup)`
  margin-bottom: 0;
  flex: 1 0 100%;
  ${Selector} {
    width: 100%;
    max-width: 50%;
    label {
      justify-content: center;
      flex-wrap: wrap;
      border: 1px solid #d9d9d9;
      background-color: #faf9f9;
      color: ${({ theme }) => theme.colors.grayscale.black};
      font-family: ${({ theme }) => theme.fontFamilies.gotham};
      padding-left: 0;
      padding-right: 0;
    }
    input:checked + label {
      border: 2px solid ${({ theme }) => theme.colors.grayscale.black};
      ${PriceLabelPrice} {
        background-color: ${({ theme }) => theme.colors.grayscale.black};
        color: white;
      }
    }
  }
`

export interface ItemVariantOptionsProps {
  item: ConfigurableProductDetailsFragment
  hasSelectedVariantCombination: boolean
  onVariantSelected: (itemValue: string) => void
  register: UseFormMethods['register']
  variantData: FormattedVariantData
  variantSelections: Record<AttributeName, AttributeValue>
  variantLabelStatus: ProductLabelStatus | undefined
}

const ItemVariantOptionsAttribute: FC<
  Pick<ItemVariantOptionsProps, 'item' | 'register' | 'variantData'> & {
    showPrice: boolean
    attribute: string
    index: number
  }
> = ({ item, register, variantData, showPrice, attribute, index }) => {
  const attributeValues = variantData.attributeValues[attribute]
  const ids = useId(attributeValues.length, `${attribute}-`)

  return (
    <div className="item-control">
      <div className="control-header robotoCondensed">{attribute}</div>
      <StyledSelectorGroup
        name={`variant.${attribute}`}
        register={register}
        selectorOptions={attributeValues.map((value, valueIndex) => ({
          id: `${ids[valueIndex]}-${value}`,
          label:
            showPrice && index === 0 ? (
              <ItemSelectorOptionLabelWithPrice
                item={item}
                label={value}
                skus={variantData.attributeProductList[attribute]?.[value] ?? []}
              />
            ) : (
              <ItemSelectorOptionLabel label={value} />
            ),
          value,
        }))}
      />
    </div>
  )
}

const ItemVariantOptions: FC<ItemVariantOptionsProps> = ({
  item,
  hasSelectedVariantCombination,
  onVariantSelected,
  register,
  variantData,
  variantSelections,
  variantLabelStatus,
}) => {
  const { usedAttributes } = variantData
  const { featureFlags } = useSiteMetadata() ?? {}
  const { isAuthenticated } = useAuth0()
  const dispensary = useDispensaryContext()
  const anonymousCart = featureFlags?.anonymousCart ?? false
  const showPrice = isAuthenticated || !!dispensary || anonymousCart

  useEffect(() => {
    if (hasSelectedVariantCombination) {
      onVariantSelected(
        // TODO: add check that nested navigation/reduction actually ends up with a leaf/string
        usedAttributes.reduce(
          (check, att) => check[variantSelections[att]] as AttributeSkuMapping,
          variantData.mapping,
        ) as unknown as string,
      )
    }
  }, [
    hasSelectedVariantCombination,
    onVariantSelected,
    usedAttributes,
    variantData.mapping,
    variantSelections,
  ])

  return (
    <SelectorGroupWrapper>
      {usedAttributes?.map((attribute, index) => (
        <ItemVariantOptionsAttribute
          key={attribute}
          item={item}
          register={register}
          variantData={variantData}
          showPrice={showPrice}
          attribute={attribute}
          index={index}
        />
      ))}
      {variantLabelStatus && (
        <ItemSelectionIndicatorMessage
          message={
            variantLabelStatus === ProductLabelStatus.Present
              ? 'LABEL ON FILE'
              : 'LABEL NOT ON FILE'
          }
        >
          <LabelStatusIcon isLabelPresent={variantLabelStatus === ProductLabelStatus.Present} />
        </ItemSelectionIndicatorMessage>
      )}
    </SelectorGroupWrapper>
  )
}

export default ItemVariantOptions
